import type { ReactNode } from 'react'
import type { BrandingConfig } from '@/types'

interface PDFPageProps {
  children: ReactNode
  pageNumber: number
  totalPages: number
  title?: string
  subtitle?: string
  branding: BrandingConfig
}

/**
 * Base A4 page wrapper for PDF generation
 * Dimensions: 210mm x 297mm (A4)
 */
export default function PDFPage({
  children,
  pageNumber,
  totalPages,
  title,
  subtitle,
  branding,
}: PDFPageProps) {
  const isLight = branding.theme === 'light'
  const bgColor = isLight ? '#ffffff' : '#1a1f2e'
  const textColor = isLight ? '#1e293b' : '#e8e6e1'
  const mutedColor = isLight ? '#64748b' : '#a8a5a0'
  const borderColor = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'

  const fontFamily =
    branding.fontFamily === 'default'
      ? "'Source Sans 3', sans-serif"
      : `'${branding.fontFamily.charAt(0).toUpperCase() + branding.fontFamily.slice(1)}', sans-serif`

  return (
    <div
      className="a4-page"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '15mm',
        backgroundColor: bgColor,
        color: textColor,
        fontFamily,
        display: 'flex',
        flexDirection: 'column',
        pageBreakAfter: 'always',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '10mm',
          borderBottom: `1px solid ${borderColor}`,
          marginBottom: '8mm',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {branding.logo && (
            <img
              src={branding.logo}
              alt={branding.companyName}
              style={{ height: '32px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }}
            />
          )}
          {branding.companyName && (
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{branding.companyName}</span>
          )}
        </div>
        {title && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', fontWeight: 600 }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: '10px', color: mutedColor }}>{subtitle}</div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>{children}</div>

      {/* Footer */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: '6mm',
          borderTop: `1px solid ${borderColor}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '10px',
          color: mutedColor,
        }}
      >
        <span>
          {branding.companyName || 'WealthSpace'} | Value of Advice Report
        </span>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
      </div>
    </div>
  )
}
